import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { AnalyticsTrigger, Button, Card, CardContent, EqualHeightProvider, Grid, GridColumn, Heading, Separator, Text } from 'telia-front-react';
import Config from '../../Config';
import GaPushOnMount from '../../content/GaPushOnMount';
import CustomerType from '../../sso/CustomerType';
import GaDataStore from '../../stores/GaDataStore';
import PersonalOffersStore from '../../stores/PersonalOffersStore';
import SsoStore from '../../stores/SsoStore';
import { twebTranslateOrEmpty } from '../../util/TranslateUtil';
import Telia1InternetCard from './Telia1InternetCard';
import Telia1MobileCard from './Telia1MobileCard';
import Telia1TvCard from './Telia1TvCard';

interface Props {
  personalOffersStore?: PersonalOffersStore;
  ssoStore?: SsoStore;
}

const Telia1OfferContainer = (props: Props): JSX.Element => {
  const { translate, translateOrEmpty } = useContext(TranslationsContext);
  const { personalOffersStore, ssoStore } = props;
  const { isMobile } = useIsMobileHook();

  const offerData = personalOffersStore.telia1PersonalOffer?.data;
  const isFullHouse = offerData && offerData.offerTv && offerData.offerMobile && offerData.offerInternet;

  const resolveTelia1SecondText = (): string => {
    const { offerMobile, offerTv, offerInternet } = offerData;
    switch (true) {
      case offerMobile && offerTv && offerInternet:
        return 'personalOffers.telia1.fullHouseSecondText';
      case offerTv && offerInternet:
        return 'personalOffers.telia1.secondText.tvInternet';
      case offerMobile && offerInternet:
        return 'personalOffers.telia1.secondText.mobileInternet';
      case offerMobile && offerTv:
        return 'personalOffers.telia1.secondText.mobileTv';
      case offerTv:
        return 'personalOffers.telia1.secondText.tv';
      case offerMobile:
        return 'personalOffers.telia1.secondText.mobile';
      default:
        return 'personalOffers.telia1.secondText.internet';
    }
  };

  const getAnalyticsProfile = (): string => {
    const getGaProfileLabel = (id: number | string) => `Profile ${id}`;
    if (!offerData?.offerIds?.length) {
      return getGaProfileLabel(6);
    }

    const { offerMobile, offerTv, offerInternet, hasTelia1MarketSegment } = offerData;

    if (ssoStore.isDealer) {
      return getGaProfileLabel(9);
    }

    switch (true) {
      case hasTelia1MarketSegment:
        return getGaProfileLabel('old');
      case offerMobile && offerTv && offerInternet:
        return getGaProfileLabel(1);
      case offerTv && offerInternet:
        return getGaProfileLabel(5);
      case offerMobile && offerInternet:
        return getGaProfileLabel(3);
      case offerMobile && offerTv:
        return getGaProfileLabel(4);
      case offerTv:
        return getGaProfileLabel(7);
      case offerMobile:
        return getGaProfileLabel(2);
      case offerInternet:
        return getGaProfileLabel(8);
      default:
        return getGaProfileLabel(6);
    }
  };

  React.useEffect(() => {
    offerData && personalOffersStore.presentSfOffers(offerData.offerIds);
  }, []);

  const renderOfferCards = (): JSX.Element => {
    const offerCards = [
      <Telia1MobileCard
        missing={!offerData?.offerMobile}
        hasOnlyCallsMobilePackage={offerData.hasOnlyCallsMobilePackage}
        isFullHouse={isFullHouse}
        key="offerCard-0"
      />,
      <Telia1InternetCard missing={!offerData?.offerInternet} isFullHouse={isFullHouse} key="offerCard-1" />,
      <Telia1TvCard missing={!offerData?.offerTv} isFullHouse={isFullHouse} key="offerCard-2" />
    ];

    return (
      <>
        {offerCards.map((offerCard, key) => (
          <GridColumn width={['lg-6', 'xl-4']} key={`telia1-offerCard.${key}`}>
            {offerCard}
          </GridColumn>
        ))}
      </>
    );
  };

  const renderOffersDescription = (): JSX.Element => {
    let ctaButton = null;

    if (isFullHouse) {
      ctaButton = (
        <>
          <AnalyticsTrigger trigger={{ id: 'Link - Telia1 full house', callback: 'onClick' }}>
            <Button theme="dark" iconRight="#arrow-right" text={translate('personalOffers.telia1.fullHouseButtonText')} url="/era/pakkumised/telia1" />
          </AnalyticsTrigger>
          <Separator spacing={'xsmall'} color={'invisible'} />
        </>
      );
    }

    return (
      <Text>
        <Heading level={1} className="h3">
          {translate('personalOffers.telia1.title')}
        </Heading>
        <p className="h5">{isFullHouse ? translate('personalOffers.telia1.fullHouseSubTitle') : translate('personalOffers.telia1.subTitle')}</p>
        {twebTranslateOrEmpty(translateOrEmpty, resolveTelia1SecondText()) && (
          <>
            <Separator spacing="xsmall" color="invisible" />
            <p className={isFullHouse ? 'h5' : 'text-small'}>{translate(resolveTelia1SecondText(), { baseUrl: Config.teliaWebUrl })}</p>
          </>
        )}
        {!!ctaButton && ctaButton}
      </Text>
    );
  };

  if (!personalOffersStore.hasTelia1Offer) {
    return ssoStore.customer.type === CustomerType.B2C && <GaPushOnMount data={{ eventLabel: getAnalyticsProfile(), eventAction: 'Telia 1 offer' }} />;
  }

  const backgroundImageUrl = '//res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/era/pakkumised/telia1/Telia_5th-Element_Deep-Purple_14_1.png';

  return (
    <>
      <Card
        theme="dark"
        isClosable={true}
        onClickClose={() => {
          personalOffersStore.rejectTelia1Offers();
          GaDataStore.pushWithoutCmsData({
            event: 'AutoEvent',
            eventCategory: 'Pakkumised',
            eventAction: 'Personal offers',
            eventLabel: 'Link - Decline - Telia1 offer'
          });
        }}
        borderRadius={isMobile ? 'small' : 'large'}
      >
        <CardContent
          backgroundImage={{
            srcset: `${backgroundImageUrl} 340w, ${backgroundImageUrl} 680w`,
            role: 'presentation',
            sources: [
              {
                minWidth: '800px',
                srcset: `${backgroundImageUrl} 800w, ${backgroundImageUrl} 1600w`
              },
              {
                minWidth: '1160px',
                srcset: `${backgroundImageUrl} 1160w, ${backgroundImageUrl} 2320w`
              }
            ]
          }}
          padding={isMobile ? 'small' : 'large'}
        >
          <GaPushOnMount data={{ eventLabel: getAnalyticsProfile(), eventAction: 'Telia 1 offer' }}>
            <EqualHeightProvider groups={['telia1-header', 'telia1-content', 'telia1-footer']}>
              <Grid>
                <GridColumn width={['lg-12', 'xl-4']}>
                  <Card background="transparent" className="text-white" padding="none">
                    <CardContent>{renderOffersDescription()}</CardContent>
                  </Card>
                </GridColumn>
                <GridColumn width={['lg-12', 'xl-8']}>
                  <Grid equalHeight={true} theme="light">
                    {renderOfferCards()}
                  </Grid>
                </GridColumn>
              </Grid>
            </EqualHeightProvider>
          </GaPushOnMount>
        </CardContent>
      </Card>
    </>
  );
};

export default inject('personalOffersStore', 'ssoStore')(observer(Telia1OfferContainer));
